import { useCallback } from 'react';
import { Image } from 'lucide-react';
import { useGooglePhotos, type GooglePhotosStatus } from '../../hooks/useGooglePhotos';

interface GooglePhotosPickerProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

const STATUS_LABELS: Record<GooglePhotosStatus, string> = {
  idle: 'Google Photos',
  'loading-sdk': 'Loading...',
  authorizing: 'Authorizing...',
  picking: 'Picking...',
  importing: 'Importing...',
  done: 'Google Photos',
  error: 'Google Photos',
};

export function GooglePhotosPicker({ onFilesSelected, disabled }: GooglePhotosPickerProps) {
  const { importPhotos, cancel, status, error } = useGooglePhotos();

  const busy = status !== 'idle' && status !== 'done' && status !== 'error';

  const handleClick = useCallback(async () => {
    if (busy) {
      cancel();
      return;
    }
    // Stream each file to the upload queue as it's downloaded
    await importPhotos((file) => {
      onFilesSelected([file]);
    });
  }, [busy, cancel, importPhotos, onFilesSelected]);

  // Don't render if VITE_GOOGLE_CLIENT_ID is not configured
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) return null;

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={disabled || (busy && status !== 'picking')}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Image size={16} />
        {busy ? STATUS_LABELS[status] : 'Google Photos'}
      </button>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
}
